/**
 * Step two: the six digits.
 *
 * This is the screen the whole design leans on, so the details are the feature.
 *
 * `autoComplete="one-time-code"` on the first box is what makes iOS and Android
 * offer the code straight from the notification — for most people that turns
 * this screen into a single tap, which is most of the "quick" the design
 * promised. The code is also in the email's subject line for the same reason.
 *
 * Typing advances, backspace on an empty box retreats, pasting six digits fills
 * all six, and the sixth digit submits on its own. Nobody should have to reach
 * for a button after typing the last character of a code they can see.
 *
 * A dead code says so and offers a resend. Leaving someone retyping a code that
 * can no longer work is the worst version of this screen.
 *
 * Two things here follow from the server sending the SAME code to an address
 * that asks again inside the window (`resendStrategy: "reuse"`). The button
 * says "send it again" rather than "send a new code", because a new code is
 * precisely what it does not produce until the old one is exhausted — and the
 * screen says how long the code lasts, because "it is still the one you have"
 * is only reassuring if you know how long that remains true.
 *
 * This screen is also restored after a reload rather than rebuilt: its owner is
 * in a mail app, and the tab they left may not survive the trip. The address
 * comes back from `pendingCode.ts`; the digits do not, and do not need to.
 *
 * Where the session allows it, "Try another way" is the exit for a code that is
 * never coming: a gateway that quarantined it, a phone with no mail app. It is
 * a quiet link that has to be opened, not a button beside the boxes, because
 * the code is still the better way in for almost everyone and a second, bigger
 * choice on this screen would pull people off it. Opening it asks for a name —
 * the facilitator is about to decide whether they recognise this person, and an
 * address alone is a poor thing to recognise someone by.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { AuthError, sendCode, verifyCode } from "./authApi.js";

const LENGTH = 6;
/**
 * The server's `OTP_POLICY.coalesceSeconds`, mirrored.
 *
 * Inside that window the server treats a repeat as the same request and sends
 * nothing, so a button that re-enabled sooner would promise a message it would
 * not produce. Matching it means the button is available again exactly when
 * pressing it does something.
 */
const RESEND_COOLDOWN_SECONDS = 20;
/**
 * The server's `OTP_POLICY.expirySeconds`, said out loud.
 *
 * Duplicated rather than imported: that module carries Better Auth with it and
 * this one runs on a phone. It is stated on screen because the reuse behaviour
 * depends on it — a person deciding whether the message they already have is
 * still the right one is asking exactly this question.
 */
const CODE_MINUTES = 15;

export interface CodeScreenProps {
  email: string;
  onVerified: () => void;
  onBack: () => void;
  /**
   * Present when the session lets a player ask the facilitator instead. Called
   * with the name they give; resolves once the request is queued, and rejects
   * with a message to show when it is refused.
   */
  onRequestAccess?: (name: string) => Promise<void>;
}

export function CodeScreen({
  email,
  onVerified,
  onBack,
  onRequestAccess,
}: CodeScreenProps): React.ReactElement {
  const [digits, setDigits] = useState<string[]>(() => Array<string>(LENGTH).fill(""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const submit = async (code: string) => {
    setBusy(true);
    setError(null);
    try {
      await verifyCode(email, code);
      onVerified();
    } catch (cause) {
      // Better Auth kills the code after three wrong guesses, so a failure here
      // may mean the code is simply gone. Say that rather than implying another
      // attempt at the same digits could work.
      setError(
        cause instanceof AuthError
          ? `${cause.message} If you have tried a few times, send it again for a fresh code.`
          : "That code did not work. Send it again for a fresh one.",
      );
      setDigits(Array<string>(LENGTH).fill(""));
      inputs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const setDigitAt = (index: number, value: string) => {
    const next = [...digits];
    next[index] = value;
    setDigits(next);

    const code = next.join("");
    if (code.length === LENGTH && next.every((digit) => digit !== "")) {
      void submit(code);
    }
  };

  const onChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (cleaned.length === 0) {
      setDigitAt(index, "");
      return;
    }

    // A pasted or autofilled code arrives in one box; spread it across all of
    // them rather than keeping only the first character.
    if (cleaned.length > 1) {
      const spread = [...digits];
      for (let offset = 0; offset < cleaned.length && index + offset < LENGTH; offset += 1) {
        spread[index + offset] = cleaned[offset]!;
      }
      setDigits(spread);
      const filled = spread.join("");
      const landing = Math.min(index + cleaned.length, LENGTH - 1);
      inputs.current[landing]?.focus();
      if (filled.length === LENGTH && spread.every((digit) => digit !== "")) void submit(filled);
      return;
    }

    setDigitAt(index, cleaned);
    if (index < LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onKeyDown = (index: number, key: string) => {
    if (key !== "Backspace") return;
    // Retreat only from an already-empty box, so the first backspace clears the
    // character you can see rather than jumping behind it.
    if (digits[index] === "" && index > 0) inputs.current[index - 1]?.focus();
  };

  const resend = async () => {
    setError(null);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    try {
      await sendCode(email);
      setDigits(Array<string>(LENGTH).fill(""));
      inputs.current[0]?.focus();
    } catch (cause) {
      if (cause instanceof AuthError) {
        setError(cause.message);
        if (cause.retryAfterSeconds) setCooldown(cause.retryAfterSeconds);
      }
    }
  };

  return (
    <div className="tq-player">
      {/* Carded for the same reason as the sign-in screen it follows: this is
          read on the machine that just asked for the code, not on a phone. */}
      <div className="tq-player-main tq-phone-card">
        <div className="tq-phone-join-title">
          <div className="tq-phone-join-icon" aria-hidden="true">
            ✉️
          </div>
          <span className="tq-phone-join-sub">CHECK YOUR EMAIL</span>
        </div>

        <p className="tq-phone-hint">
          We sent a six-digit code to <strong>{email}</strong>. It works for {CODE_MINUTES} minutes.
        </p>

        <div
          style={{ display: "flex", gap: 8, justifyContent: "center", margin: "18px 0" }}
          role="group"
          aria-label="Six-digit code"
        >
          {digits.map((digit, index) => (
            <input
              // The boxes are positional and never reorder, so the index is a
              // stable identity here.
              key={index}
              ref={(element) => {
                inputs.current[index] = element;
              }}
              className="tq-phone-code-input"
              style={{ width: 48, textAlign: "center", padding: "12px 0", fontSize: 24 }}
              type="text"
              inputMode="numeric"
              // Only the first box carries this: it is what lets the OS offer
              // the code from the notification.
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={LENGTH}
              value={digit}
              disabled={busy}
              aria-label={`Digit ${index + 1}`}
              onChange={(event) => onChange(index, event.target.value)}
              onKeyDown={(event) => onKeyDown(index, event.key)}
            />
          ))}
        </div>

        <div className="tq-phone-hint" role="alert">
          {busy ? "Checking…" : error}
        </div>

        <button
          type="button"
          className="tq-phone-secondary"
          onClick={() => void resend()}
          disabled={cooldown > 0 || busy}
          style={{ minHeight: 48 }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Send it again"}
        </button>

        <button
          type="button"
          className="tq-phone-secondary"
          onClick={onBack}
          disabled={busy}
          style={{ minHeight: 48, marginTop: 8 }}
        >
          Use a different email
        </button>

        {onRequestAccess ? <AnotherWay email={email} onRequestAccess={onRequestAccess} /> : null}
      </div>
    </div>
  );
}

/** "Try another way": collapsed to a link until someone needs it. */
function AnotherWay({
  email,
  onRequestAccess,
}: {
  email: string;
  onRequestAccess: (name: string) => Promise<void>;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) nameInput.current?.focus();
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (name.trim().length === 0) return;
    setSending(true);
    setError(null);
    try {
      await onRequestAccess(name.trim());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That didn't go through. Try again.");
      setSending(false);
    }
  };

  return (
    <div style={{ marginTop: 20, textAlign: "center" }}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="tq-another-way"
        style={{
          background: "none",
          border: "none",
          color: "inherit",
          opacity: 0.85,
          textDecoration: "underline",
          fontSize: 15,
          cursor: "pointer",
          padding: 8,
        }}
      >
        Try another way
      </button>

      {open ? (
        <form
          id="tq-another-way"
          onSubmit={submit}
          style={{ marginTop: 8, textAlign: "left" }}
          aria-label="Request access from the facilitator"
        >
          <p className="tq-phone-hint" style={{ color: "inherit", fontWeight: 400 }}>
            Code not arriving? The facilitator can let you in as <strong>{email}</strong>. Tell them
            who you are:
          </p>
          <label className="tq-phone-label" htmlFor="tq-access-name">
            YOUR NAME
          </label>
          <input
            id="tq-access-name"
            ref={nameInput}
            className="tq-phone-code-input"
            style={{ fontSize: 18, letterSpacing: "normal", textTransform: "none" }}
            type="text"
            autoComplete="name"
            maxLength={80}
            placeholder="First and last name"
            value={name}
            disabled={sending}
            required
            onChange={(event) => setName(event.target.value)}
          />
          <div className="tq-phone-hint" role="alert">
            {error}
          </div>
          <button
            type="submit"
            className="tq-phone-primary"
            disabled={sending || name.trim().length === 0}
            style={{ minHeight: 52, width: "100%" }}
          >
            {sending ? "Asking…" : "Request access from the facilitator"}
          </button>
        </form>
      ) : null}
    </div>
  );
}
